import React from 'react';
import { Card, Icon } from "semantic-ui-react";
import moment from "moment";
import { formatCurrency, sumKeyValueOfArray } from "../../utils/commonutils";
import { adpLink } from "../../constants"
import { showFinancialNotesTable } from "./functions";
import PayDetailDateCell from './PayDetailDateCell';

// GO GA: each practice's worked days are split into GA days (scheduled with the 'GA' speciality)
// and non-GA days; each group is paid the greater of its bCare × payOverBase% or its days × its
// daily rate. The per-practice breakdown comes from goGaSummary on the practice report data.
const GoGa = ({ providerData, startDate, earnings, details, currentPeriod }) => {
    const { numberOfWorkedDays, patientVisits, patientVisitsPerDay, contractType, dentistWorkTimeType, prodAdjWo, sortName, financialNoteList, financialNotes } = providerData;
    const { payChecks, payDetailsList, goGaSummaries = [] } = details;
    const annualizedPay = earnings * (currentPeriod != null ? 24 : 12);
    const avgDailyEarnings = numberOfWorkedDays ? earnings / numberOfWorkedDays : 0;
    const payDayValue = currentPeriod ? currentPeriod.paidOn : moment(startDate).add(1, 'months').format('MMMM YYYY');
    const workPeriodValue = currentPeriod ? currentPeriod.displayRange : moment(startDate).format('MMMM YYYY');

    const payOverBase = goGaSummaries[0]?.payOverBase;
    const gaDays = goGaSummaries.reduce((sum, s) => sum + s.ga.days, 0);
    const nonGaDays = goGaSummaries.reduce((sum, s) => sum + s.nonGa.days, 0);
    const dayRateBasePay = goGaSummaries.reduce((sum, s) => sum + s.ga.basePay + s.nonGa.basePay, 0);
    const goGaTotalPay = goGaSummaries.reduce((sum, s) => sum + s.totalPay, 0);

    const contractDetailsCard = (
        <div className="ui card reports-card">
            <div className="content">
                <h5>Contract Details</h5>
                <ul className="data-list">
                    <li><p>Contract Type</p><p>{contractType}</p></li>
                    <li><p>GA Day Rate</p><p>{formatCurrency(goGaSummaries[0]?.gaDailyRate || 0)}</p></li>
                    <li><p>Non GA Day Rate</p><p>{formatCurrency(goGaSummaries[0]?.nonGaDailyRate || 0)}</p></li>
                    <li><p>Percentage of Billable Care</p><p>{payOverBase ?? '-'}%</p></li>
                    <li><p>Gross to Billable Adjustment Percentage</p><p>{100 - prodAdjWo}</p></li>
                    <li><p>Work Status</p><p>{dentistWorkTimeType}</p></li>
                </ul>
            </div>
        </div>
    );

    const groupRows = goGaSummaries.flatMap(summary => [['GA', summary.ga], ['Non GA', summary.nonGa]]
        .map(([dayType, group]) => ({ practiceName: summary.practiceName, dayType, ...group, bCareWins: group.bCarePay > group.basePay })));

    return (
        <div>
            <div className="row">
                <div className="col-12 col-md-6 col-lg-4">
                    <div className="ui card reports-card">
                        <div className="content">
                            <h5>{sortName}</h5>
                            <ul className="data-list">
                                <li><p>{currentPeriod != null ? 'Pay Day' : 'Pay Month'}</p><p>{payDayValue}</p></li>
                                <li><p>{currentPeriod != null ? 'Work Period' : 'Work Month'}</p><p>{workPeriodValue}</p></li>
                                <li><p>Work Days</p><p>{numberOfWorkedDays}</p></li>
                                <li><p>GA Days</p><p>{gaDays}</p></li>
                                <li><p>Non GA Days</p><p>{nonGaDays}</p></li>
                                <li><p>Day Rate Base Pay</p><p>{formatCurrency(dayRateBasePay)}</p></li>
                                <li><p>Total Month Earnings</p><p>{formatCurrency(earnings)}</p></li>
                                <li><p>Annualized Earnings</p><p>{formatCurrency(annualizedPay)}</p></li>
                                <li><p>Avg Daily Earnings</p><p>{formatCurrency(avgDailyEarnings)}</p></li>
                                <li><p>Pt Visits</p><p>{patientVisits}</p></li>
                                <li><p>Pt Visits/ Day </p><p>{patientVisitsPerDay}</p></li>
                            </ul>
                        </div>
                    </div>
                    {currentPeriod == null && contractDetailsCard}
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                    {currentPeriod != null && contractDetailsCard}

                    {currentPeriod == null && (
                        <div className="ui card reports-card">
                            <div className="content">
                                {payChecks && Object.entries(payChecks).map(([date, dateChecks], i) => (
                                    <div key={i}>
                                        <div className="d-flex justify-content-between flex-wrap">
                                            <h5 className="mr10">{i + 1}{(1 + i) === 1 ? 'st' : 'nd'} Friday Gross Earnings Pre-Tax</h5>
                                            <strong className="secondaryColor text-right">{date}</strong>
                                        </div>
                                        <table className="table table-striped table-hover table-sm mt-0 mb-0">
                                            <thead>
                                                <tr><th>OFFICE</th><th className="text-right">EARNINGS</th></tr>
                                            </thead>
                                            <tbody>
                                                {dateChecks.officeChecks.map((office, officeKey) => (
                                                    <tr key={officeKey}>
                                                        <td>{office.practice}</td>
                                                        <td className="text-right">{formatCurrency(office.check)}</td>
                                                    </tr>
                                                ))}
                                                <tr>
                                                    <td><strong>Total</strong></td>
                                                    <td className="text-right"><strong>{formatCurrency(dateChecks.total)}</strong></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {providerData.teamContribution && <Card
                        meta={<b>Team Contribution</b>}
                        description={<b className={providerData.teamContribution > 0 ? 'text-green' : 'text-red'}><br />{formatCurrency(providerData.teamContribution)}</b>}
                    />}

                    <div className="ui card reports-card">
                        <div className="content">
                            <h5>Accessing Your Pay Stub</h5>
                            <ul className="data-list">
                                <li>
                                    <p>Link to ADP</p>
                                    <p><a href={adpLink} target={'_blank'} rel="noopener noreferrer"><Icon name='external alternate' className="primaryColor" /></a></p>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* GA / Non GA pay calculation */}
            <div className="table-responsive">
                <h5>GA / Non GA Pay</h5>
                <table className="table table-striped table-hover">
                    <thead>
                        <tr>
                            <th><div>PRACTICE</div></th>
                            <th><div>DAY TYPE</div></th>
                            <th><div>DAYS</div></th>
                            <th><div>BILLABLE CARE</div></th>
                            <th><div>{payOverBase}% PAY</div></th>
                            <th><div>DAY RATE</div></th>
                            <th><div>BASE PAY</div></th>
                            <th><div>PAID</div></th>
                        </tr>
                    </thead>
                    <tbody>
                        {groupRows.map((row, rowKey) => (
                            <tr key={rowKey}>
                                <td>{row.practiceName}</td>
                                <td>{row.dayType}</td>
                                <td>{row.days}</td>
                                <td>{formatCurrency(row.bCare)}</td>
                                <td>{row.bCareWins ? <b>{formatCurrency(row.bCarePay)}</b> : formatCurrency(row.bCarePay)}</td>
                                <td>{formatCurrency(row.rate)}</td>
                                <td>{row.bCareWins ? formatCurrency(row.basePay) : <b>{formatCurrency(row.basePay)}</b>}</td>
                                <td><b>{formatCurrency(row.pay)}</b></td>
                            </tr>
                        ))}
                        <tr>
                            <td><b>Total</b></td>
                            <td></td>
                            <td><b>{gaDays + nonGaDays}</b></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td><b>{formatCurrency(dayRateBasePay)}</b></td>
                            <td><b>{formatCurrency(goGaTotalPay)}</b></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {showFinancialNotesTable(financialNoteList, financialNotes)}

            {/* Pay Details Table */}
            <div className="table-responsive">
                <h5>Pay Details</h5>
                <table className="table table-striped table-hover">
                    <thead>
                        <tr>
                            <th><div>SERVICE DATE</div></th>
                            <th><div>PRACTICE</div></th>
                            <th><div>DAY TYPE</div></th>
                            <th><div>DAY RATE</div></th>
                            <th><div>GROSS CARE</div></th>
                            <th><div>ADJUSTMENT</div></th>
                            <th><div>WRITE OFF</div></th>
                            <th><div>BILLABLE CARE</div></th>
                            <th><div>FINANCIAL NOTES</div></th>
                            <th><div>EARNINGS</div></th>
                        </tr>
                    </thead>
                    <tbody>
                        {(payDetailsList || []).map((iRow, rowKey) => (
                            <tr key={rowKey}>
                                <td><PayDetailDateCell row={iRow} /></td>
                                <td>{iRow.practice}</td>
                                <td>{iRow.goGaDayType || ''}</td>
                                <td>{iRow.goGaDayRate != null ? formatCurrency(iRow.goGaDayRate) : ''}</td>
                                <td>{formatCurrency(iRow.care)}</td>
                                <td>{formatCurrency(iRow.adjustmentPrice)}</td>
                                <td>{formatCurrency(iRow.writeOffPrice * -1)}</td>
                                <td>{formatCurrency(iRow.billableCare)} <b>{iRow.billableNotes ? ' ** ' : ''}</b></td>
                                <td>{formatCurrency(iRow.financialNotes)}</td>
                                <td>{formatCurrency(iRow.totalPay)}</td>
                            </tr>
                        ))}
                        <tr>
                            <td><b>Total</b></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td><b>{formatCurrency(sumKeyValueOfArray(payDetailsList, 'care'))}</b></td>
                            <td><b>{formatCurrency(sumKeyValueOfArray(payDetailsList, 'adjustmentPrice'))}</b></td>
                            <td><b>{formatCurrency(sumKeyValueOfArray(payDetailsList, 'writeOffPrice') * -1)}</b></td>
                            <td><b>{formatCurrency(sumKeyValueOfArray(payDetailsList, 'billableCare'))}</b></td>
                            <td><b>{formatCurrency(sumKeyValueOfArray(payDetailsList, 'financialNotes'))}</b></td>
                            <td><b>{formatCurrency(sumKeyValueOfArray(payDetailsList, 'totalPay'))}</b></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default GoGa;
